import { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Phone, Shield, CheckCircle2, AlertCircle, ArrowRight, Loader2, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function GBPPhoneSetup({ getHeaders }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/client/dashboard/gbp/phone-config`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setConfig(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getHeaders]);

  const applyConfig = async () => {
    setApplying(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/client/dashboard/gbp/phone-config/apply`, {
        method: 'POST',
        headers: getHeaders(),
      });
      const data = await res.json();
      setResult(data);
      if (data.success) {
        // Refresh config
        const r2 = await fetch(`${API}/api/client/dashboard/gbp/phone-config`, { headers: getHeaders() });
        const c2 = await r2.json();
        setConfig(c2);
      }
    } catch {
      setResult({ success: false, message: 'Failed to apply. Please try again.' });
    }
    setApplying(false);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-blue-400 animate-spin" /></div>;
  if (!config) return null;

  const hasTrackingNumber = config.tracking_phone && !config.tracking_phone.includes('No ');
  const hasRealPhone = config.real_phone && !config.real_phone.includes('No ');
  const isAlreadyConfigured = config.current_gbp_phone &&
    config.current_gbp_phone.primary &&
    config.current_gbp_phone.primary.replace(/\D/g, '').slice(-10) === (config.tracking_phone || '').replace(/\D/g, '').slice(-10);

  return (
    <div className="space-y-4" data-testid="gbp-phone-setup">
      {/* SEO Explanation Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-900">SEO-Safe Phone Configuration</p>
              <p className="text-xs text-blue-700 mt-1 leading-relaxed">
                Google allows tracking numbers on your Business Profile if configured correctly.
                Set the <strong>tracking number as Primary</strong> (customers see and call this) and your
                <strong> real number as Additional</strong> (Google uses this to match NAP across citations).
                This preserves your local SEO rankings while enabling call attribution.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current vs Recommended */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Current GBP Phone */}
        <Card className="border-gray-200">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Current GBP Phone</p>
            {config.current_gbp_phone ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                  <div>
                    <p className="text-[10px] text-gray-500">Primary (visible to customers)</p>
                    <p className="text-sm font-mono font-semibold text-gray-900">{config.current_gbp_phone.primary || 'Not set'}</p>
                  </div>
                  <Phone className="w-4 h-4 text-gray-400" />
                </div>
                <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                  <div>
                    <p className="text-[10px] text-gray-500">Additional (NAP consistency)</p>
                    <p className="text-sm font-mono font-semibold text-gray-900">
                      {config.current_gbp_phone.additional?.length > 0 ? config.current_gbp_phone.additional.join(', ') : 'Not set'}
                    </p>
                  </div>
                  <Phone className="w-4 h-4 text-gray-300" />
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-500 italic">Unable to read current phone numbers from GBP. They can be checked at <a href="https://business.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">business.google.com</a></p>
            )}
          </CardContent>
        </Card>

        {/* Recommended Config */}
        <Card className={`border-2 ${isAlreadyConfigured ? 'border-green-300 bg-green-50' : 'border-indigo-300 bg-indigo-50'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {isAlreadyConfigured ? 'Current Config (Correct!)' : 'Recommended Config'}
              </p>
              {isAlreadyConfigured && <CheckCircle2 className="w-4 h-4 text-green-600" />}
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between bg-white/80 rounded-lg p-3 border border-indigo-100">
                <div>
                  <p className="text-[10px] text-indigo-500 font-medium">Primary → Tracking Number</p>
                  <p className="text-sm font-mono font-bold text-indigo-900">
                    {hasTrackingNumber ? config.tracking_phone : <span className="text-amber-600 text-xs">Not assigned yet</span>}
                  </p>
                </div>
                <Badge className="bg-indigo-100 text-indigo-700 text-[9px]">Customers call this</Badge>
              </div>
              <div className="flex items-center justify-between bg-white/80 rounded-lg p-3 border border-indigo-100">
                <div>
                  <p className="text-[10px] text-indigo-500 font-medium">Additional → Real Business Number</p>
                  <p className="text-sm font-mono font-bold text-indigo-900">
                    {hasRealPhone ? config.real_phone : <span className="text-amber-600 text-xs">Not set in Business Profile</span>}
                  </p>
                </div>
                <Badge className="bg-gray-100 text-gray-600 text-[9px]">NAP consistency</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Apply Button */}
      {hasTrackingNumber && hasRealPhone && !isAlreadyConfigured && (
        <Card className="border-gray-200">
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <ArrowRight className="w-5 h-5 text-indigo-600" />
              <div>
                <p className="text-sm font-semibold text-gray-900">Apply Recommended Configuration</p>
                <p className="text-xs text-gray-500">This will update the phone numbers on your Google Business Profile listing</p>
              </div>
            </div>
            <Button
              onClick={applyConfig}
              disabled={applying}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
              data-testid="apply-phone-config-btn"
            >
              {applying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying...</> : <><RefreshCw className="w-4 h-4 mr-2" /> Apply to GBP</>}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Missing requirements */}
      {(!hasTrackingNumber || !hasRealPhone) && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Setup Required</p>
                <ul className="text-xs text-amber-700 mt-1 space-y-1">
                  {!hasRealPhone && <li>Add your real business phone number in the <strong>Business Profile</strong> tab</li>}
                  {!hasTrackingNumber && <li>A <strong>Google/GBP</strong> channel tracking number needs to be assigned by your account manager</li>}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Result */}
      {result && (
        <Card className={`border ${result.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
          <CardContent className="p-4 flex items-start gap-3">
            {result.success ? <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />}
            <div>
              <p className={`text-sm font-semibold ${result.success ? 'text-green-800' : 'text-red-800'}`}>{result.message}</p>
              {result.success && (
                <p className="text-xs text-green-600 mt-1">Primary: {result.primary} | Additional: {result.additional}</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Already configured */}
      {isAlreadyConfigured && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-green-800">Phone numbers are correctly configured!</p>
              <p className="text-xs text-green-600 mt-1">Your GBP listing has the tracking number as primary and real number as additional. SEO and call attribution are both working optimally.</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
